import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CurrentPlanCard from './components/CurrentPlanCard';
import UsageMetricsCard from './components/UsageMetricsCard';
import BillingHistoryTable from './components/BillingHistoryTable';
import PaymentMethodCard from './components/PaymentMethodCard';
import UpgradeOptionsGrid from './components/UpgradeOptionsGrid';
import CommunityAccessCard from './components/CommunityAccessCard';
import SubscriptionActionsCard from './components/SubscriptionActionsCard';

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const [currentTier, setCurrentTier] = useState('free');
  const [quotaUsage, setQuotaUsage] = useState({ used: 0, total: 100 });
  const [booksCreated, setBooksCreated] = useState(3);
  const [aiGenerations, setAiGenerations] = useState(47);
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false);

  const [invoices] = useState([
    {
      id: 1,
      date: '12/04/2025',
      description: 'Professional Plan - Monthly',
      amount: 99.00,
      status: 'paid'
    },
    {
      id: 2,
      date: '11/04/2025',
      description: 'Professional Plan - Monthly',
      amount: 99.00,
      status: 'paid'
    },
    {
      id: 3,
      date: '10/04/2025',
      description: 'Starter Plan - Monthly',
      amount: 49.00,
      status: 'paid'
    },
    {
      id: 4,
      date: '09/04/2025',
      description: 'Starter Plan - Monthly',
      amount: 49.00,
      status: 'paid'
    }
  ]);

  const [paymentMethod] = useState({
    brand: 'visa',
    last4: '4242',
    expiry: '12/2027'
  });

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    if (!isAuthenticated) {
      navigate('/user-login');
      return;
    }

    const tier = localStorage.getItem('subscriptionTier') || 'free';
    const quota = JSON.parse(localStorage.getItem('quotaUsage') || '{"used":65,"total":100}');
    
    setCurrentTier(tier);
    setQuotaUsage(quota);
  }, [navigate]);

  const handleUpgrade = () => {
    window.scrollTo({ top: document.getElementById('upgrade-section')?.offsetTop || 0, behavior: 'smooth' });
  };

  const handleSelectPlan = (planId) => {
    setCurrentTier(planId);
    localStorage.setItem('subscriptionTier', planId);
    
    const newQuotas = {
      starter: { used: 0, total: 100 },
      professional: { used: 0, total: 400 },
      enterprise: { used: 0, total: 999999 }
    };
    
    const newQuota = newQuotas?.[planId] || { used: 0, total: 100 };
    setQuotaUsage(newQuota);
    localStorage.setItem('quotaUsage', JSON.stringify(newQuota));
    
    setShowUpgradeSuccess(true);
    setTimeout(() => setShowUpgradeSuccess(false), 5000);
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdatePayment = (newPaymentInfo) => {
    console.log('Payment method updated:', newPaymentInfo);
  };

  const handlePauseSubscription = () => {
    console.log('Subscription paused');
  };

  const handleCancelSubscription = () => {
    console.log('Subscription cancelled');
  };

  const handleReactivateSubscription = () => {
    console.log('Subscription reactivated');
  };

  const hasAccess = currentTier !== 'free';
  const communityUrl = 'https://www.skool.com/authorpreneur-ai';

  return (
    <>
      <Helmet>
        <title>Subscription Management - AuthorPreneur AI</title>
        <meta name="description" content="Manage your AuthorPreneur AI subscription, view usage metrics, update billing information, and upgrade your plan for more book creation capabilities." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20 pb-16">
          <div className="container mx-auto px-4 max-w-7xl">
            {showUpgradeSuccess && (
              <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center space-x-3 animate-fade-in">
                <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Subscription Updated Successfully!</p>
                  <p className="text-xs text-muted-foreground">Your new plan is now active and credits have been reset.</p>
                </div>
                <button
                  onClick={() => setShowUpgradeSuccess(false)}
                  className="p-1 hover:bg-success/20 rounded transition-colors duration-200"
                >
                  <Icon name="X" size={16} className="text-muted-foreground" />
                </button>
              </div>
            )}

            <div className="mb-8">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-4">
                <button
                  onClick={() => navigate('/user-dashboard')}
                  className="hover:text-foreground transition-colors duration-200"
                >
                  Dashboard
                </button>
                <Icon name="ChevronRight" size={14} />
                <span className="text-foreground">Subscription Management</span>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                    Subscription Management
                  </h1>
                  <p className="text-muted-foreground">
                    Manage your plan, track usage, and update billing information
                  </p>
                </div>

                <Button
                  variant="outline"
                  size="default"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={() => navigate('/user-dashboard')}
                >
                  Back to Dashboard
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2">
                <CurrentPlanCard currentTier={currentTier} onUpgrade={handleUpgrade} />
              </div>

              <div>
                <UsageMetricsCard
                  quotaUsage={quotaUsage}
                  booksCreated={booksCreated}
                  aiGenerations={aiGenerations}
                />
              </div>
            </div>

            <div className="mb-8" id="upgrade-section">
              <UpgradeOptionsGrid currentTier={currentTier} onSelectPlan={handleSelectPlan} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <PaymentMethodCard paymentMethod={paymentMethod} onUpdate={handleUpdatePayment} />
              <CommunityAccessCard hasAccess={hasAccess} communityUrl={communityUrl} />
            </div>

            <div className="mb-8">
              <BillingHistoryTable invoices={invoices} />
            </div>

            <div>
              <SubscriptionActionsCard
                currentTier={currentTier}
                onPause={handlePauseSubscription}
                onCancel={handleCancelSubscription}
                onReactivate={handleReactivateSubscription}
              />
            </div>
          </div>
        </main>

        <footer className="bg-card border-t border-border py-8">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                © {new Date()?.getFullYear()} AuthorPreneur AI. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
                  Privacy Policy
                </button>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
                  Terms of Service
                </button>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
                  Contact Support
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SubscriptionManagement;